/**
 * Base Sensor Extension
 *
 * Abstract base class that all hardware sensor extensions inherit from.
 * Provides common functionality:
 *   - Standard pin menu (digital pins 2-13, analog A0-A5)
 *   - Pin validation
 *   - Auto-registration of code templates into the CodeTemplateRegistry
 *   - Consistent getInfo() structure
 *
 * ── How to create a new sensor ─────────────────────────────────
 *
 *   1. Create a new folder: src/lib/hardware-sensors/my-sensor/
 *   2. Create index.js extending BaseSensorExtension
 *   3. Override `getSensorInfo()` to return { id, name, blocks, menus, codeTemplates }
 *   4. Import and instantiate in the extension loader
 *
 * The base class will merge your sensor-specific info with
 * the default pin menus and register code templates automatically.
 */

import codeTemplateRegistry from './code-template-registry';
import formatMessage from 'format-message';

// ── Default menus shared by all sensors ────────────────────────

const DIGITAL_PIN_MENU = [
    {text: '2', value: '2'},
    {text: '3', value: '3'},
    {text: '4', value: '4'},
    {text: '5', value: '5'},
    {text: '6', value: '6'},
    {text: '7', value: '7'},
    {text: '8', value: '8'},
    {text: '9', value: '9'},
    {text: '10', value: '10'},
    {text: '11', value: '11'},
    {text: '12', value: '12'},
    {text: '13', value: '13'}
];

const ANALOG_PIN_MENU = [
    {text: 'A0', value: 'A0'},
    {text: 'A1', value: 'A1'},
    {text: 'A2', value: 'A2'},
    {text: 'A3', value: 'A3'},
    {text: 'A4', value: 'A4'},
    {text: 'A5', value: 'A5'}
];

const ALL_PIN_MENU = [...DIGITAL_PIN_MENU, ...ANALOG_PIN_MENU];

const HIGH_LOW_MENU = [
    {text: 'HIGH', value: 'HIGH'},
    {text: 'LOW', value: 'LOW'}
];

// ── Base class ─────────────────────────────────────────────────

class BaseSensorExtension {
    constructor (runtime) {
        this.runtime = runtime;
        this._registered = false;
    }

    /**
     * Override in subclass. Return sensor-specific metadata.
     *
     * @returns {{
     *   id: string,
     *   name: string,
     *   color1: string,
     *   color2: string,
     *   color3: string,
     *   blocks: object[],
     *   menus: object,
     *   codeTemplates: object[]
     * }}
     */
    getSensorInfo () {
        throw new Error('Subclass must implement getSensorInfo()');
    }

    /**
     * Standard Scratch extension entry point.
     * Merges sensor-specific info with shared defaults.
     */
    getInfo () {
        const sensor = this.getSensorInfo();

        // Auto-register code templates the first time getInfo is called
        if (!this._registered && sensor.codeTemplates) {
            const prefixedTemplates = sensor.codeTemplates.map(template => {
                const prefix = `${sensor.id}_`;
                const opcode = template.opcode.startsWith(prefix) ?
                    template.opcode :
                    `${prefix}${template.opcode}`;
                return Object.assign({}, template, {opcode});
            });
            codeTemplateRegistry.registerAll(prefixedTemplates);
            this._registered = true;
        }

        return {
            id: sensor.id,
            name: sensor.name,
            color1: sensor.color1 || '#FF6F00',
            color2: sensor.color2 || '#E65100',
            color3: sensor.color3 || '#BF360C',
            blocks: sensor.blocks,
            menus: Object.assign({}, this._getDefaultMenus(), sensor.menus || {})
        };
    }

    /**
     * Default menus available to all sensor extensions.
     * Sensor-specific menus are merged on top.
     */
    _getDefaultMenus () {
        return {
            digitalPinMenu: {
                acceptReporters: true,
                items: DIGITAL_PIN_MENU
            },
            analogPinMenu: {
                acceptReporters: true,
                items: ANALOG_PIN_MENU
            },
            allPinMenu: {
                acceptReporters: true,
                items: ALL_PIN_MENU
            },
            highLowMenu: {
                acceptReporters: false,
                items: HIGH_LOW_MENU
            }
        };
    }

    // ── Shared block handler stubs ─────────────────────────────
    // These run inside the VM for live preview on the stage.
    // Real hardware interaction happens via the hardware-uploader
    // and the generated code. These just return simulated defaults.

    /**
     * Simulate a digital read (returns 0 or 1 randomly for preview)
     */
    simulateDigitalRead () {
        return Math.random() > 0.5 ? 1 : 0;
    }

    /**
     * Simulate an analog read (returns 0-1023 randomly for preview)
     */
    simulateAnalogRead () {
        return Math.floor(Math.random() * 1024);
    }
}

export default BaseSensorExtension;
export {
    DIGITAL_PIN_MENU,
    ANALOG_PIN_MENU,
    ALL_PIN_MENU,
    HIGH_LOW_MENU
};
